import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GenericView, SearchInput, ActionButtons } from '../../../components/common/table';

const SalesListDetail = ({
    salesListDetailData,
    isLoading,
    searchQuerySalesListDetail,
    setSearchQuerySalesListDetail,
    handleExportExcelSalesListDetail,
    formatCurrencyValue,
    filters // Add filters prop
}) => {
    const navigate = useNavigate();

    // Function to navigate to invoice page with filters
    const navigateToInvoice = (status = null) => {
        // Get date range from filters (assuming filters has period and date range info)
        let dateRange = { startDate: null, endDate: null };

        if (filters && filters.period) {
            // Import the date range calculation function (or inline it)
            const getDateRangeFromPeriod = (period, customStartDate, customEndDate) => {
                if (period === 'custom' && customStartDate && customEndDate) {
                    return { startDate: customStartDate, endDate: customEndDate };
                }

                const now = new Date();
                const currentYear = now.getFullYear();
                const currentMonth = now.getMonth();

                let startDate, endDate;

                switch (period) {
                    case 'this_year':
                        startDate = new Date(currentYear, 0, 1);
                        endDate = new Date(currentYear, 11, 31);
                        break;
                    case 'last_year':
                        startDate = new Date(currentYear - 1, 0, 1);
                        endDate = new Date(currentYear - 1, 11, 31);
                        break;
                    case 'this_month':
                        startDate = new Date(currentYear, currentMonth, 1);
                        endDate = new Date(currentYear, currentMonth + 1, 0);
                        break;
                    case 'last_month':
                        startDate = new Date(currentYear, currentMonth - 1, 1);
                        endDate = new Date(currentYear, currentMonth, 0);
                        break;
                    case 'this_quarter':
                        const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
                        startDate = new Date(currentYear, quarterStartMonth, 1);
                        endDate = new Date(currentYear, quarterStartMonth + 3, 0);
                        break;
                    case 'last_quarter':
                        const lastQuarterStartMonth = Math.floor(currentMonth / 3) * 3 - 3;
                        startDate = new Date(currentYear, lastQuarterStartMonth, 1);
                        endDate = new Date(currentYear, lastQuarterStartMonth + 3, 0);
                        break;
                    default:
                        // Default to this year
                        startDate = new Date(currentYear, 0, 1);
                        endDate = new Date(currentYear, 11, 31);
                }

                // Format dates as YYYY-MM-DD
                const formatDate = (date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                };

                return {
                    startDate: formatDate(startDate),
                    endDate: formatDate(endDate)
                };
            };

            dateRange = getDateRangeFromPeriod(
                filters.period,
                filters.startDate,
                filters.endDate
            );
        }

        // Build query parameters
        const params = new URLSearchParams();
        if (dateRange.startDate) params.append('billDateFrom', dateRange.startDate);
        if (dateRange.endDate) params.append('billDateTo', dateRange.endDate);

        // Carry over the active Sales Creator filter — maps to Invoice's "Created By" filter
        // (Invoice's own assignedTo param filters by the linked Project.assignedTo, a different field).
        if (Array.isArray(filters?.salesCreator) && filters.salesCreator.length > 0) {
            params.append('createdById', filters.salesCreator[0]);
        }

        // If status is provided, add status filter
        if (status) {
            // Handle both single status and array of statuses
            if (Array.isArray(status)) {
                status.forEach(s => params.append('status', s));
            } else {
                params.append('status', status);
            }
        }

        // Navigate to invoice page with query parameters
        navigate(`/invoice?${params.toString()}`);
    };
    // Calculate metrics from sales list detail data
    const metrics = useMemo(() => {
        const totalInvoices = salesListDetailData.length;
        const totalRevenue = salesListDetailData.reduce((sum, item) => sum + (item.nilaiInvoice || 0), 0);

        const paidInvoices = salesListDetailData.filter(item => item.statusBayar === 'Paid');
        const paidCount = paidInvoices.length;
        const paidRevenue = paidInvoices.reduce((sum, item) => sum + (item.nilaiInvoice || 0), 0);

        const partialInvoices = salesListDetailData.filter(item => item.statusBayar === 'Partial');
        const partialCount = partialInvoices.length;
        const partialRevenue = partialInvoices.reduce((sum, item) => sum + (item.nilaiInvoice || 0), 0);

        const unpaidInvoices = salesListDetailData.filter(item => item.statusBayar === 'Unpaid');
        const unpaidCount = unpaidInvoices.length;
        const unpaidRevenue = unpaidInvoices.reduce((sum, item) => sum + (item.nilaiInvoice || 0), 0);

        const paymentRate = totalRevenue > 0 ? (paidRevenue / totalRevenue) * 100 : 0;

        return {
            totalInvoices,
            totalRevenue,
            paidCount,
            paidRevenue,
            partialCount,
            partialRevenue,
            unpaidCount,
            unpaidRevenue,
            paymentRate
        };
    }, [salesListDetailData]);

    // Filter data by search query
    const filteredSalesListDetailData = salesListDetailData.filter(item => {
        if (!searchQuerySalesListDetail) return true;
        const query = searchQuerySalesListDetail.toLowerCase();
        return (
            item.invoiceId?.toLowerCase().includes(query) ||
            item.pelanggan?.toLowerCase().includes(query) ||
            item.salesCreator?.toLowerCase().includes(query) ||
            item.tagging?.toLowerCase().includes(query) ||
            item.statusBayar?.toLowerCase().includes(query)
        );
    });

    // Table columns configuration
    const salesListDetailColumns = [
        {
            key: 'invoiceId',
            label: 'INVOICE NUMBER',
            className: 'text-truncate',
            sortValue: (item) => item.invoiceId || '',
            render: (invoiceId, row) => row.invoiceNumericId ? (
                <a
                    href={`/invoice/${row.invoiceNumericId}`}
                    className="text-primary text-truncate d-inline-block text-decoration-none"
                    style={{ cursor: 'pointer', maxWidth: '150px', width: '150px' }}
                    title={invoiceId}
                    onClick={(e) => {
                        if (e.ctrlKey || e.metaKey || e.button === 1) return;
                        e.preventDefault();
                        navigate(`/invoice/${row.invoiceNumericId}`);
                    }}
                    onAuxClick={(e) => { if (e.button === 1) e.stopPropagation(); }}
                >
                    {invoiceId || '-'}
                </a>
            ) : (
                <span className="text-truncate d-inline-block" style={{ maxWidth: '150px', width: '150px' }} title={invoiceId}>
                    {invoiceId || '-'}
                </span>
            ),
            sortable: true
        },
        {
            key: 'tanggal',
            label: 'DATE',
            className: 'text-truncate',
            sortValue: (item) => item.tanggal || '',
            render: (tanggal) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '120px', width: '120px' }}
                    title={tanggal}
                >
                    {tanggal || '-'}
                </span>
            ),
            sortable: true
        },
        {
            key: 'pelanggan',
            label: 'CUSTOMER',
            className: 'text-truncate',
            sortValue: (item) => item.pelanggan || '',
            render: (pelanggan, row) => row.companyId ? (
                <a
                    href={`/clients/${row.companyId}`}
                    className="text-primary text-truncate d-inline-block text-decoration-none"
                    style={{ cursor: 'pointer', maxWidth: '200px', width: '200px' }}
                    title={pelanggan}
                    onClick={(e) => {
                        if (e.ctrlKey || e.metaKey || e.button === 1) return;
                        e.preventDefault();
                        navigate(`/clients/${row.companyId}`);
                    }}
                    onAuxClick={(e) => { if (e.button === 1) e.stopPropagation(); }}
                >
                    {pelanggan || '-'}
                </a>
            ) : (
                <span className="text-truncate d-inline-block" style={{ maxWidth: '200px', width: '200px' }} title={pelanggan}>
                    {pelanggan || '-'}
                </span>
            ),
            sortable: true
        },
        {
            key: 'tagging',
            label: 'TAGGING',
            className: 'text-truncate',
            sortable: false,
            render: (value) => {
                const tags = value ? String(value).split(',').map(s => s.trim()).filter(Boolean) : [];
                if (tags.length === 0) return <span className="text-muted">-</span>;
                return (
                    <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
                        {tags.map((tag, i) => (
                            <span key={i} className="badge bg-blue-lt text-wrap" title={tag} style={{ fontSize: '0.7rem' }}>
                                {tag}
                            </span>
                        ))}
                    </span>
                );
            }
        },
        {
            key: 'salesCreator',
            label: 'SALES PERSON',
            className: 'text-truncate',
            sortValue: (item) => item.salesCreator || '',
            render: (salesCreator) => (
                <span
                    className="text-truncate d-inline-block"
                    style={{ maxWidth: '180px', width: '180px' }}
                    title={salesCreator}
                >
                    {salesCreator || '-'}
                </span>
            ),
            sortable: true
        },
        {
            key: 'nilaiInvoice',
            label: 'INVOICE VALUE (IDR)',
            className: 'text-truncate',
            sortValue: (item) => item.nilaiInvoice || 0,
            render: (nilaiInvoice) => {
                const formattedValue = `Rp ${formatCurrencyValue(nilaiInvoice || 0)}`;
                return (
                    <span
                        className="text-truncate d-inline-block"
                        style={{ textAlign: 'right', maxWidth: '180px', width: '180px' }}
                        title={formattedValue}
                    >
                        {formattedValue}
                    </span>
                );
            },
            sortable: true
        },
        {
            key: 'statusBayar',
            label: 'PAYMENT STATUS',
            className: 'text-truncate',
            sortValue: (item) => item.statusBayar || '',
            render: (statusBayar) => {
                let statusClass = 'text-success'; // Paid (green)
                if (statusBayar === 'Unpaid') {
                    statusClass = 'text-warning'; // Unpaid (orange)
                } else if (statusBayar === 'Partial') {
                    statusClass = 'text-warning'; // Partial (orange)
                }

                return (
                    <span
                        className={`text-truncate d-inline-block ${statusClass}`}
                        style={{ maxWidth: '120px', width: '120px' }}
                        title={statusBayar}
                    >
                        {statusBayar || '-'}
                    </span>
                );
            },
            sortable: true
        }
    ];

    return (
        <>
            {/* Title */}
            <h3 className="mb-3">Sales List Detail</h3>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-primary"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Total Invoices</div>
                                <div className="h3 mb-0">Rp {formatCurrencyValue(metrics.totalRevenue)}</div>
                                <div
                                    className="h3 text-primary"
                                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                                    onClick={() => navigateToInvoice()}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title="Click to view all invoices"
                                >
                                    {metrics.totalInvoices} Invoice
                                </div>
                            </div>
                            <div className="text-muted small">Total number of invoices in this period.</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-success"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Paid Invoices (Fully Paid)</div>
                                <div className="h3 mb-0">Rp {formatCurrencyValue(metrics.paidRevenue)}</div>
                                <div
                                    className="h3 text-success"
                                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                                    onClick={() => navigateToInvoice('PAID')}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title="Click to view paid invoices"
                                >
                                    {metrics.paidCount} Invoice
                                </div>
                            </div>
                            <div className="text-muted small">Number of invoices that have been fully paid.</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-yellow"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Partial Invoices</div>
                                <div className="h3 mb-0">Rp {formatCurrencyValue(metrics.partialRevenue)}</div>
                                <div
                                    className="h3 text-yellow"
                                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                                    onClick={() => navigateToInvoice('PARTIAL')}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title="Click to view partial invoices"
                                >
                                    {metrics.partialCount} Invoice
                                </div>
                            </div>
                            <div className="text-muted small">Number of invoices with partial payments.</div>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card h-100">
                        <div className="card-status-start bg-danger"></div>
                        <div className="card-body d-flex flex-column">
                            <div className="flex-fill">
                                <div className="text-muted mb-2">Unpaid Invoices</div>
                                <div className="h3 mb-0">Rp {formatCurrencyValue(metrics.unpaidRevenue)}</div>
                                <div
                                    className="h3 text-danger"
                                    style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                                    onClick={() => navigateToInvoice(['SENT', 'FINAL'])}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                    title="Click to view unpaid invoices"
                                >
                                    {metrics.unpaidCount} Invoice
                                </div>
                            </div>
                            <div className="text-muted small">Number of invoices that have not been paid.</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="card">
                <div className="card-header">
                    <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                        <li className="nav-item-tab" role="presentation">
                            <a href="#tabs-list-data-detail" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                        </li>
                    </ul>
                </div>
                <div className="card-body">
                    <div className="tab-content">
                        <div className="tab-pane active show" id="tabs-list-data-detail" role="tabpanel">
                            <GenericView
                                data={filteredSalesListDetailData}
                                columns={salesListDetailColumns}
                                isLoading={isLoading}
                                emptyStateMessage="No sales list detail data found"
                                searchComponent={
                                    <SearchInput
                                        value={searchQuerySalesListDetail}
                                        onChange={setSearchQuerySalesListDetail}
                                        placeholder="Search invoice ID, customer, sales creator, or status..."
                                    />
                                }
                                actionButtons={
                                    <ActionButtons
                                        onExportExcel={handleExportExcelSalesListDetail}
                                    />
                                }
                                showPagination={true}
                                initialPageSize={10}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SalesListDetail;

